/**
 * O que faz: Hooks para buscar relatórios de análise de chat, gerenciar sugestões de prompt e
 *            disparar análises manuais.
 * Onde usa: ChatAnalysisSection e PromptSuggestionCard no painel de configurações de IA.
 * Conexões: Tabelas chat_analysis_reports, prompt_improvement_suggestions.
 *           RPCs: apply_prompt_suggestion, dismiss_prompt_suggestion.
 *           Edge function: analyze-chat-insights (disparo manual).
 * Autor: IA
 * Data: 24/04/2026
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { ChatAnalysisReport, ChatAnalysisConfig, PromptImprovementSuggestion } from '@/types/chatAnalysis';

// ===========================================================================
// BUSCA DE RELATÓRIOS
// ===========================================================================

/** Retorna os últimos N relatórios de análise da clínica, do mais recente ao mais antigo. */
export const useChatAnalysisReports = (clinicaId: string | undefined, limit = 5) =>
  useQuery<ChatAnalysisReport[]>({
    queryKey: ['chat-analysis-reports', clinicaId],
    enabled: !!clinicaId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chat_analysis_reports')
        .select('*')
        .eq('clinica_id', clinicaId!)
        .order('criado_em', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as ChatAnalysisReport[];
    },
  });

// ===========================================================================
// SUGESTÕES DE MELHORIA DE PROMPT
// ===========================================================================

/** Retorna sugestões pendentes de melhoria de prompt para a clínica. */
export const usePromptSuggestions = (clinicaId: string | undefined) =>
  useQuery<PromptImprovementSuggestion[]>({
    queryKey: ['prompt-suggestions', clinicaId],
    enabled: !!clinicaId,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('prompt_improvement_suggestions')
        .select('*')
        .eq('clinica_id', clinicaId!)
        .eq('status', 'pendente')
        .order('criado_em', { ascending: false })
        .limit(3);
      if (error) throw error;
      return (data ?? []) as PromptImprovementSuggestion[];
    },
  });

// ===========================================================================
// APLICAR SUGESTÃO
// ===========================================================================

/** Aplica a sugestão de prompt: atualiza ai_clinica_prompt e marca como 'aplicado'. */
export const useApplyPromptSuggestion = (clinicaId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (suggestionId: string) => {
      const { error } = await supabase.rpc('apply_prompt_suggestion', {
        p_suggestion_id: suggestionId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prompt-suggestions', clinicaId] });
      qc.invalidateQueries({ queryKey: ['clinica-ai-settings', clinicaId] });
      toast.success('Prompt atualizado com sucesso!');
    },
    onError: (e: Error) => toast.error(`Erro ao aplicar sugestão: ${e.message}`),
  });
};

// ===========================================================================
// IGNORAR SUGESTÃO
// ===========================================================================

/** Descarta a sugestão de prompt sem aplicar. */
export const useDismissPromptSuggestion = (clinicaId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (suggestionId: string) => {
      const { error } = await supabase.rpc('dismiss_prompt_suggestion', {
        p_suggestion_id: suggestionId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['prompt-suggestions', clinicaId] });
      toast.success('Sugestão descartada.');
    },
    onError: (e: Error) => toast.error(`Erro ao descartar: ${e.message}`),
  });
};

// ===========================================================================
// DISPARAR ANÁLISE MANUAL
// ===========================================================================

/** Chama a edge function analyze-chat-insights manualmente para uma clínica específica. */
export const useTriggerManualAnalysis = (clinicaId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!clinicaId) throw new Error('ID da clínica não encontrado');

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Sessão expirada');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/analyze-chat-insights`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ clinica_id: clinicaId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Erro ao iniciar análise');
      // Verifica se pelo menos uma clínica foi processada com sucesso
      const resultados = data.resultados as Record<string, string> | undefined;
      const statuses = Object.values(resultados ?? {});
      const algumOk = statuses.some((s) => s === 'ok');
      const motivo = statuses[0];
      if (!algumOk) {
        const msgs: Record<string, string> = {
          pulado_cota: 'Cota mensal de análises atingida.',
          pulado_inatividade: 'Sem mensagens suficientes no período para análise.',
          pulado_circuit_breaker: 'Limite global de análises atingido. Tente amanhã.',
        };
        if (motivo?.startsWith('erro:')) throw new Error(motivo.replace(/^erro:\s*/, ''));
        throw new Error(msgs[motivo] ?? data.message ?? 'Análise não processada.');
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-analysis-reports', clinicaId] });
      qc.invalidateQueries({ queryKey: ['prompt-suggestions', clinicaId] });
      qc.invalidateQueries({ queryKey: ['analysis-quota', clinicaId] });
      toast.success('Análise concluída! Relatório disponível abaixo.');
    },
    onError: (e: Error) => toast.error(`Erro na análise: ${e.message}`),
  });
};

// ===========================================================================
// SALVAR CONFIGURAÇÃO DE ANÁLISE
// ===========================================================================

/** Persiste os campos de configuração de análise na tabela clinicas. */
export const useSaveChatAnalysisConfig = (clinicaId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: Partial<ChatAnalysisConfig>) => {
      if (!clinicaId) throw new Error('ID da clínica não encontrado');
      const { error } = await supabase
        .from('clinicas')
        .update({ ...config, updated_at: new Date().toISOString() })
        .eq('id', clinicaId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-analysis-config', clinicaId] });
      toast.success('Configuração de análise salva!');
    },
    onError: (e: Error) => toast.error(`Erro ao salvar: ${e.message}`),
  });
};

/** Busca a configuração de análise da clínica. */
export const useChatAnalysisConfig = (clinicaId: string | undefined) =>
  useQuery<ChatAnalysisConfig>({
    queryKey: ['chat-analysis-config', clinicaId],
    enabled: !!clinicaId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinicas')
        .select('analise_chat_ativo, analise_ultima_execucao')
        .eq('id', clinicaId!)
        .single();
      if (error) throw error;
      return data as ChatAnalysisConfig;
    },
  });

// ===========================================================================
// COTA MENSAL DE ANÁLISES
// ===========================================================================

export interface AnalysisQuota {
  runsThisMonth: number;
  resetAt: string | null;
}

/** Busca quantas análises a clínica já rodou no mês atual. */
export const useAnalysisQuota = (clinicaId: string | undefined) =>
  useQuery<AnalysisQuota>({
    queryKey: ['analysis-quota', clinicaId],
    enabled: !!clinicaId,
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinicas')
        .select('analysis_runs_this_month, analysis_runs_reset_at')
        .eq('id', clinicaId!)
        .single();
      if (error) throw error;
      return {
        runsThisMonth: data?.analysis_runs_this_month ?? 0,
        resetAt: data?.analysis_runs_reset_at ?? null,
      };
    },
  });
